import { useCallback, useMemo, useState } from "react"

import type { ActiveGridFilter } from "../utils/gridColumnFilters"

export type ColumnFilterState<T extends string> = Record<T, string | null>

export function useGridColumnFilters<T extends string>(
  emptyFilters: ColumnFilterState<T>,
  labels: Record<T, string>,
) {
  const [columnFilters, setColumnFilters] =
    useState<ColumnFilterState<T>>(emptyFilters)
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)

  const handleRemoveColumnFilter = useCallback((key: string) => {
    setColumnFilters((prev) => ({
      ...prev,
      [key]: null,
    }))
  }, [])

  const handleClearColumnFilters = useCallback(() => {
    setColumnFilters(emptyFilters)
  }, [emptyFilters])

  const handleApplyColumnFilters = useCallback(
    (values: Record<string, string | null>) => {
      setColumnFilters({
        ...emptyFilters,
        ...(Object.fromEntries(
          (Object.keys(emptyFilters) as T[]).map((key) => [
            key,
            values[key] ?? null,
          ]),
        ) as ColumnFilterState<T>),
      })
    },
    [emptyFilters],
  )

  const activeColumnFilters = useMemo<ActiveGridFilter[]>(() => {
    return (Object.entries(columnFilters) as [T, string | null][])
      .filter(([, value]) => Boolean(value))
      .map(([key, value]) => ({
        key,
        label: labels[key],
        value: value as string,
      }))
  }, [columnFilters, labels])

  const activeColumnFilterCount = activeColumnFilters.length

  return {
    columnFilters,
    setColumnFilters,
    filterPanelOpen,
    setFilterPanelOpen,
    activeColumnFilters,
    activeColumnFilterCount,
    handleRemoveColumnFilter,
    handleClearColumnFilters,
    handleApplyColumnFilters,
  }
}
